import React, { useState } from "react";
import { ArrowRight, Clock, Sparkles, Heart } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { BUSINESS } from "../lib/constants";
import TrustStrip from "../components/TrustStrip";
import FaqSection from "../components/FaqSection";
import BookingCTA from "../components/BookingCTA";
import SEOHead from "../components/SEOHead";
import Lightbox from "../components/Lightbox";
import BrowComparison from "../components/BrowComparison";

// Services that should show the brow comparison section
const BROW_SERVICES = ["nanoBrows", "powderBrows", "comboBrows", "browLamination"];

/**
 * Reusable service page template.
 * Props: serviceKey (translation key), gallery (image URLs), heroImage (optional override)
 */
const ServicePage = ({ serviceKey, gallery, heroImage }) => {
    const { t } = useLanguage();
    const s = t.services[serviceKey];
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const showBrowComparison = BROW_SERVICES.includes(serviceKey);
    
    // Map serviceKey to slug for comparison component
    const serviceSlugMap = {
        nanoBrows: "nano-brows",
        powderBrows: "powder-brows",
        comboBrows: "combo-brows",
        browLamination: "brow-lamination",
    };

    const openLightbox = (index) => setLightboxIndex(index);
    const closeLightbox = () => setLightboxIndex(null);
    const nextImage = () => setLightboxIndex((prev) => (prev + 1) % gallery.length);
    const prevImage = () => setLightboxIndex((prev) => (prev - 1 + gallery.length) % gallery.length);

    return (
        <>
            <SEOHead page={serviceKey} />
            {/* Hero */}
            <section
                data-testid={`service-hero-${serviceKey}`}
                className="relative min-h-[68vh] flex items-end overflow-hidden"
            >
                <div className="absolute inset-0">
                    <img src={heroImage} alt={`${s.title} Las Vegas - permanent makeup results by Wendy Chester PMUA`} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3D3D24] via-[#3D3D24]/55 to-[#3D3D24]/15" />
                </div>
                <div className="relative w-full max-w-[1400px] mx-auto px-6 pb-16 md:pb-20 z-10">
                    <div className="max-w-3xl">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{s.overline}</div>
                        <h1 className="font-serif text-[#FFFFF3] text-5xl md:text-7xl leading-[0.98] mb-6">
                            {s.title}
                        </h1>
                        <p className="text-[#FFFFF3]/90 text-base md:text-lg max-w-2xl leading-relaxed">
                            {s.subtitle}
                        </p>
                    </div>
                </div>
            </section>

            <TrustStrip variant="dark" />

            {/* Description */}
            <section className="bg-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1100px] mx-auto px-6 grid lg:grid-cols-[1.4fr_1fr] gap-14 lg:gap-20">
                    <div>
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">The Service</div>
                        <p className="font-serif text-2xl md:text-3xl text-[#3D3D24] leading-snug mb-8">
                            {s.description}
                        </p>
                        <div className="border-l-2 border-[#D4A95A] pl-6">
                            <div className="font-label text-[10px] text-[#3D3D24]/70 mb-2">Best For</div>
                            <p className="text-[#3D3D24]/85 leading-relaxed">{s.bestFor}</p>
                        </div>
                    </div>
                    <aside className="bg-[#3D3D24] text-[#FFFFF3] p-10 self-start">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-6">At a Glance</div>
                        <div className="space-y-6">
                            <div className="flex items-start gap-4 pb-6 border-b border-[#FFFFF3]/15">
                                <Sparkles size={18} className="text-[#D4A95A] mt-1 shrink-0" />
                                <div>
                                    <div className="font-label text-[10px] opacity-70">Pricing</div>
                                    <div className="text-base mt-1">{s.pricing}</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-4 pb-6 border-b border-[#FFFFF3]/15">
                                <Clock size={18} className="text-[#D4A95A] mt-1 shrink-0" />
                                <div>
                                    <div className="font-label text-[10px] opacity-70">Duration</div>
                                    <div className="text-base mt-1">{s.duration}</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Heart size={18} className="text-[#D4A95A] mt-1 shrink-0" />
                                <div>
                                    <div className="font-label text-[10px] opacity-70">Healing</div>
                                    <div className="text-base mt-1">{s.healing}</div>
                                </div>
                            </div>
                        </div>
                        <a
                            data-testid={`service-book-${serviceKey}`}
                            href="/contact"
                            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#D4A95A] text-[#3D3D24] px-6 py-4 font-label text-xs hover:bg-[#FFFFF3] transition-colors"
                        >
                            {t.common.bookConsultation}
                            <ArrowRight size={14} />
                        </a>
                        <div className="text-[10px] font-label text-center mt-4 opacity-65 tracking-[0.2em]">
                            {t.common.depositNote}
                        </div>
                    </aside>
                </div>
            </section>

            {/* Gallery */}
            {gallery && gallery.length > 0 && (
                <section
                    data-testid={`service-gallery-${serviceKey}`}
                    className="bg-[#F5F2E4] py-24 md:py-32"
                >
                    <div className="max-w-[1400px] mx-auto px-6">
                        <div className="mb-12">
                            <div className="font-label text-[11px] text-[#D4A95A] mb-5">
                                {t.home.galleryOverline}
                            </div>
                            <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05]">
                                {t.common.gallery} — {s.title}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            {gallery.map((src, i) => (
                                <div
                                    key={i}
                                    data-testid={`gallery-${serviceKey}-${i}`}
                                    className="overflow-hidden aspect-[4/5] bg-[#F5F2E4] cursor-pointer group"
                                    onClick={() => openLightbox(i)}
                                >
                                    <img
                                        src={src}
                                        alt={`${s.title} Las Vegas before and after results ${i + 1} - Beauty Ink LV`}
                                        className="w-full h-full object-cover bink-crop-bottom group-hover:scale-105 transition-transform duration-300"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Lightbox */}
            {lightboxIndex !== null && (
                <Lightbox
                    images={gallery}
                    currentIndex={lightboxIndex}
                    onClose={closeLightbox}
                    onNext={nextImage}
                    onPrev={prevImage}
                />
            )}

            {/* Brow Comparison - only for brow services */}
            {showBrowComparison && (
                <BrowComparison currentService={serviceSlugMap[serviceKey]} />
            )}

            <FaqSection />
            <BookingCTA variant="dark" testid={`booking-cta-${serviceKey}`} />
        </>
    );
};

export default ServicePage;
